import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {filter, Observable, switchMap, take, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {SnackBarService} from "../component/snackbar/snackbar-service";
import {MsalBroadcastService, MsalService} from "@azure/msal-angular";
import {InteractionStatus} from "@azure/msal-browser";

export interface ApiOptions {
  httpParams?: HttpParams;
  errorSnackBarMessage?: string | null; // null suppresses the error snackbar entirely
  responseType?: 'json' | 'blob';
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly apiUrl: string = environment.apiUrl;
  private readonly headers = new HttpHeaders({'Content-Type': 'application/json'});

  constructor(
      private readonly _msalService: MsalService,
      private readonly _msalBroadcastService: MsalBroadcastService,
      private readonly _http: HttpClient,
      private readonly _snackBarService: SnackBarService
  ) {
  }

  request<T>(method: string, endpoint: string, body: any = null, apiOptions?: ApiOptions): Observable<T> {
    const tryRequest = () => {
      const url = `${this.apiUrl}${endpoint}`;
      return this._http.request<T>(method, url, {
        body,
        headers: this.headers,
        params: apiOptions?.httpParams,
        responseType: apiOptions?.responseType ?? 'json' as any
      }).pipe(
          catchError((error) => {
            const errorMessage = error.error?.message ?? `Error: ${error.status} - ${error.statusText}`;
            console.error(errorMessage)
            if (apiOptions?.errorSnackBarMessage !== null) {
              this._snackBarService.showErrorSnackBar(apiOptions?.errorSnackBarMessage ?? `HTTP Error: ${method} ${url}`)
            }
            return throwError(() => new Error(errorMessage));
          })
      );
    }

    if (this._msalService?.instance?.getActiveAccount() == null) {
      return this._handleNotLoggedIn<T>(tryRequest, endpoint);
    }

    return tryRequest();
  }

  private _handleNotLoggedIn<T>(tryRequest: () => Observable<T>, endpoint: string) {
    return this._msalBroadcastService.inProgress$.pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        take(1),
        switchMap(() => {
          if (this._msalService?.instance?.getActiveAccount() != null) {
            return tryRequest();
          } else {
            console.error(`No active account after MSAL interaction completed - ${endpoint}`);
            this._snackBarService.showErrorSnackBar("Login Error");
            return throwError(() => new Error(`No active account while api call - ${endpoint}`));
          }
        })
    );
  }

  get<T>(endpoint: string, apiOptions?: ApiOptions): Observable<T> {
    return this.request<T>('GET', endpoint, null, apiOptions);
  }

  post<T>(endpoint: string, data?: any, apiOptions?: ApiOptions): Observable<T> {
    return this.request<T>('POST', endpoint, data, apiOptions);
  }

  patch<T>(endpoint: string, data?: any, apiOptions?: ApiOptions): Observable<T> {
    return this.request<T>('PATCH', endpoint, data, apiOptions);
  }

  put<T>(endpoint: string, data: any, apiOptions?: ApiOptions): Observable<T> {
    return this.request<T>('PUT', endpoint, data, apiOptions);
  }

  delete<T>(endpoint: string, apiOptions?: ApiOptions): Observable<T> {
    return this.request<T>('DELETE', endpoint, apiOptions);
  }

  deleteWithData<T>(endpoint: string, data: any, apiOptions?: ApiOptions): Observable<T> {
    return this.request<T>('DELETE', endpoint, data, apiOptions);
  }
}
